// Google Fonts API helpers — fetch catalogue, filter, build/parse CSS values.

export interface GoogleFont {
  family: string;
  category: string;
}

interface GoogleFontsApiResponse {
  items: Array<{ family: string; category: string; variants: string[] }>;
}

let cachedFonts: GoogleFont[] | null = null;

/**
 * Fetch the Google Fonts catalogue sorted by popularity. The result is cached
 * in a module-level variable so subsequent calls are instant.
 */
export async function fetchGoogleFonts(apiKey: string): Promise<GoogleFont[]> {
  if (cachedFonts) return cachedFonts;

  const url = `https://www.googleapis.com/webfonts/v1/webfonts?key=${encodeURIComponent(apiKey)}&sort=popularity`;
  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Google Fonts API returned ${res.status}`);
  }

  const data: GoogleFontsApiResponse = await res.json();
  cachedFonts = data.items.map(({ family, category }) => ({ family, category }));
  return cachedFonts;
}

/** Case-insensitive substring match on the family name. */
export function filterFonts(fonts: GoogleFont[], query: string): GoogleFont[] {
  const q = query.trim().toLowerCase();
  if (q.length < 3) return [];
  return fonts.filter((f) => f.family.toLowerCase().includes(q));
}

const GENERIC_FALLBACK: Record<string, string> = {
  "sans-serif": "sans-serif",
  serif: "serif",
  monospace: "monospace",
  display: "sans-serif",
  handwriting: "sans-serif",
};

/**
 * Build a CSS `font-family` value: the family name is quoted, followed by a
 * generic fallback derived from the font's category.
 *
 * Example: `buildFontValue("Playfair Display", "serif")` → `"Playfair Display", serif`
 */
export function buildFontValue(family: string, category: string): string {
  const fallback = GENERIC_FALLBACK[category] ?? "sans-serif";
  return `"${family}", ${fallback}`;
}

/**
 * Parse a saved CSS `font-family` token value like `"Roboto", sans-serif`
 * into its constituent parts.
 */
export function parseFontValue(
  value: string
): { family: string; fallback: string } | null {
  if (!value) return null;

  // Try to extract a quoted family name first.
  const quoted = value.match(/^"([^"]+)"/);
  if (quoted) {
    const rest = value.slice(quoted[0].length).replace(/^[,\s]+/, "").trim();
    return { family: quoted[1], fallback: rest || "sans-serif" };
  }

  // Unquoted: take the first comma-separated segment.
  const segments = value.split(",").map((s) => s.trim());
  if (segments.length === 0 || !segments[0]) return null;
  return {
    family: segments[0],
    fallback: segments.slice(1).join(", ") || "sans-serif",
  };
}
